import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "../../Components/Button";
import CKEditorFull from "../../Components/CKEditor";
import ChapterForm from "../../Components/ChapterForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Components/Tabs";
import { COURSE_CATEGORY_OPTIONS, COURSE_STATUS_OPTIONS, DEFAULT_COURSE_CATEGORY, Course, CourseStatus, isStandaloneCourseCategory } from "../../Types/CourseTypes";
import { CourseProductCardDto, CreateProductCard, GetProductCardByCourseId, SyncProductCardStripe, UpdateProductCard, uploadProductCardImage } from "../../DataService/ProductCardService";
import { CreateCourse, UpdateCourse } from "../../DataService/CourseService";
import CourseProductCard from "./AdminCourseComp/CourseProductCard";

type Props = {
  course: Course;
  onBack: () => void;
  onSaved: (course: Course) => void;
};

const emptyProductCard = (): CourseProductCardDto => ({
  courseId: 0,
  price: "",
  rating: 0,
  instructor: "",
  thumbnailImageUrl: "",
  bannerImageUrl: "",
  status: 0
});

const getCourseStatusLabel = (status: CourseStatus) => {
  switch (status) {
    case CourseStatus.Published:
      return "Publicerad";
    case CourseStatus.Archived:
      return "Arkiverad";
    default:
      return "Utkast";
  }
};

const getCourseStatusTone = (status: CourseStatus) => {
  switch (status) {
    case CourseStatus.Published:
      return "emerald";
    case CourseStatus.Archived:
      return "slate";
    default:
      return "amber";
  }
};

export const AdminCourseEditor = ({ course, onBack, onSaved }: Props) => {
  const navigate = useNavigate();
  const [workingCourse, setWorkingCourse] = useState<Course>(structuredClone(course));
  const [snapshotCourse, setSnapshotCourse] = useState<Course>(structuredClone(course));
  const [productCard, setProductCard] = useState<CourseProductCardDto>(emptyProductCard());
  const [snapshotProductCard, setSnapshotProductCard] = useState<CourseProductCardDto>(emptyProductCard());
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingStripe, setIsSyncingStripe] = useState(false);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(workingCourse) !== JSON.stringify(snapshotCourse) ||
      JSON.stringify(productCard) !== JSON.stringify(snapshotProductCard);
  }, [workingCourse, snapshotCourse, productCard, snapshotProductCard]);

  useEffect(() => {
    const cloned = structuredClone({
      ...course,
      category: course.category ?? DEFAULT_COURSE_CATEGORY,
      isStandalone: isStandaloneCourseCategory(course.category ?? DEFAULT_COURSE_CATEGORY),
    });
    setWorkingCourse(cloned);
    setSnapshotCourse(cloned);

    if (course.id > 0) {
      if (course.isStandalone) {
        GetProductCardByCourseId(course.id)
          .then((res) => {
            if (!res) return;
            const next = { ...emptyProductCard(), ...res, courseId: course.id };
            setProductCard(next);
            setSnapshotProductCard(structuredClone(next));
          })
          .catch(() => {
            const next = emptyProductCard();
            next.courseId = course.id;
            setProductCard(next);
            setSnapshotProductCard(structuredClone(next));
          });
      } else {
        const next = emptyProductCard();
        next.courseId = course.id;
        setProductCard(next);
        setSnapshotProductCard(structuredClone(next));
      }
    } else {
      const next = emptyProductCard();
      setProductCard(next);
      setSnapshotProductCard(structuredClone(next));
    }
  }, [course]);

  const updateCourseField = <K extends keyof Course>(key: K, value: Course[K]) => {
    setWorkingCourse(prev => ({ ...prev, [key]: value }));
  };

  const updateCategory = (category: string) => {
    setWorkingCourse((prev) => ({
      ...prev,
      category,
      isStandalone: isStandaloneCourseCategory(category),
    }));
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const saveCourse = async () => {
    try {
      setIsSaving(true);

      let savedCourse = workingCourse;

      if (workingCourse.id === 0) {
        const createdId = await CreateCourse({
          title: workingCourse.title,
          description: workingCourse.description,
          category: workingCourse.category ?? DEFAULT_COURSE_CATEGORY,
          status: workingCourse.status,
          isStandalone: isStandaloneCourseCategory(workingCourse.category ?? DEFAULT_COURSE_CATEGORY),
          chapters: []
        });

        if (!createdId) throw new Error("Kunde inte skapa kurs");

        savedCourse = { ...workingCourse, id: createdId };
      } else {
        await UpdateCourse(workingCourse);
      }

      if (savedCourse.id > 0 && savedCourse.isStandalone) {
        try {
          await UpdateProductCard(savedCourse.id, {
            courseId: savedCourse.id,
            price: productCard.price,
            rating: productCard.rating,
            instructor: productCard.instructor,
            thumbnailImageUrl: productCard.thumbnailImageUrl,
            bannerImageUrl: productCard.bannerImageUrl,
            status: productCard.status
          });
        } catch (productCardError) {
          try {
            await CreateProductCard({
              courseId: savedCourse.id,
              price: productCard.price,
              rating: productCard.rating,
              instructor: productCard.instructor,
              thumbnailImageUrl: productCard.thumbnailImageUrl,
              bannerImageUrl: productCard.bannerImageUrl,
              status: productCard.status
            });
          } catch (createError) {
            console.warn("Product card save failed", createError);
          }
        }
      } else if (savedCourse.id > 0) {
        const next = emptyProductCard();
        next.courseId = savedCourse.id;
        setProductCard(next);
        setSnapshotProductCard(structuredClone(next));
      }

      setWorkingCourse(structuredClone(savedCourse));
      setSnapshotCourse(structuredClone(savedCourse));
      setProductCard(prev => ({ ...prev, courseId: savedCourse.id }));
      setSnapshotProductCard(prev => ({ ...prev, courseId: savedCourse.id }));
      onSaved(savedCourse);
    } finally {
      setIsSaving(false);
    }
  };

  const totalDuration = workingCourse.totalDurationSeconds
    ? `${Math.round(workingCourse.totalDurationSeconds / 60)} min`
    : "—";

  const chapterCount = workingCourse.chapters.length;
  const stripeSynced = Boolean(productCard.stripeProductId);
  const saveStateLabel = hasUnsavedChanges ? "Osparade ändringar" : "Alla ändringar sparade";
  const productStatusLabel = getCourseStatusLabel(productCard.status as CourseStatus);

  const handleBack = () => {
    if (hasUnsavedChanges && !window.confirm("Du har osparade ändringar. Vill du lämna kurseditorn ändå?")) return;
    onBack();
  };

  const handlePreview = () => {
    if (workingCourse.id === 0) return;
    if (hasUnsavedChanges && !window.confirm("Du har osparade ändringar. Vill du öppna förhandsgranskningen ändå?")) return;
    navigate(`/admin/course-preview/${workingCourse.id}`);
  };

  const syncStripe = async () => {
    if (workingCourse.id === 0) {
      toast.warning("Spara kursen först innan du synkar Stripe.");
      return;
    }

    if (!workingCourse.isStandalone) {
      toast.warning("Interna kurser har inget produktkort att synka.");
      return;
    }

    try {
      setIsSyncingStripe(true);
      const synced = await SyncProductCardStripe(workingCourse.id);
      if (!synced) {
        toast.error("Kunde inte synka Stripe");
        return;
      }

      setProductCard(structuredClone(synced));
      setSnapshotProductCard(structuredClone(synced));
      toast.success("Stripe synkad");
    } catch {
      toast.error("Kunde inte synka Stripe");
    } finally {
      setIsSyncingStripe(false);
    }
  };

  const updateImage = async (type: "thumbnail" | "banner", file?: File) => {
    if (!file) return;

    if (workingCourse.id === 0) {
      toast.warning("Spara kursen först innan du laddar upp bilder.");
      return;
    }

    const url = await uploadProductCardImage(workingCourse.id, file, type);
    if (!url) {
      toast.error("Kunde inte ladda upp bilden");
      return;
    }

    setProductCard(prev => ({
      ...prev,
      [type === "thumbnail" ? "thumbnailImageUrl" : "bannerImageUrl"]: url
    }));
  };

  return (
    <div className="ac-v2-editor">
      <header className="ac-v2-editor-header ac-v2-editor-hero">
        <div className="ac-v2-editor-hero__copy">
          <button className="ac-v2-back-button" onClick={handleBack}>← Tillbaka till kurslistan</button>

          <div>
            <div className="ac-v2-editor-kicker">Kurseditor</div>
            <span className="ac-v2-editor-pill ac-v2-editor-pill--slate" style={{ marginTop: 8, display: "inline-flex" }}>
              {workingCourse.isStandalone ? "Marketplace" : workingCourse.category === "__system-rot" ? "ROT" : "GROW"}
            </span>
            <h2>{workingCourse.id === 0 ? "Ny kurs" : workingCourse.title}</h2>
            <p>{workingCourse.id === 0 ? "Skapa kursen innan du lägger till kapitel" : "Hantera kursens innehåll och produktkort"}</p>
          </div>
        </div>

        <div className="ac-v2-editor-hero__actions">
          <div className="ac-v2-editor-status-rail">
            <span className={`ac-v2-editor-pill ac-v2-editor-pill--${getCourseStatusTone(workingCourse.status)}`}>
              {getCourseStatusLabel(workingCourse.status)}
            </span>
            <span className="ac-v2-editor-pill">{chapterCount} kapitel</span>
            <span className={`ac-v2-editor-pill ${stripeSynced ? "ac-v2-editor-pill--emerald" : "ac-v2-editor-pill--amber"}`}>
              Stripe: {stripeSynced ? "Synkad" : "Inte synkad"}
            </span>
            <span className="ac-v2-editor-pill ac-v2-editor-pill--slate">{totalDuration}</span>
          </div>

          <div className="ac-v2-editor-actions">
            <span className={`ac-v2-editor-save-state ${hasUnsavedChanges ? "dirty" : "clean"}`}>{saveStateLabel}</span>
            {workingCourse.id > 0 && (
              <Button
                onClick={syncStripe}
                disabled={hasUnsavedChanges || isSyncingStripe}
                text={
                  isSyncingStripe
                    ? "Synkar Stripe..."
                    : hasUnsavedChanges
                      ? "Spara först"
                      : productCard.stripeProductId
                        ? "Synka Stripe igen"
                        : "Synka Stripe"
                }
                variant="secondary"
              />
            )}
            <Button onClick={saveCourse} text={isSaving ? "Sparar..." : "Spara kurs"} />
            {workingCourse.id > 0 && (
              <button type="button" className="ac-v2-course-preview-link" onClick={handlePreview}>
                Öppna i CoursePlayer
              </button>
            )}
          </div>
        </div>
      </header>

      <Tabs defaultValue="basics" className="ac-v2-editor-tabs">
        <div className="ac-v2-editor-tabs__header">
          <div>
            <h3>Redigera kursen</h3>
            <p>Byt mellan grundinfo, produktkort och innehåll utan att lämna editorn.</p>
          </div>
          <TabsList>
          <TabsTrigger value="basics">Grundinfo</TabsTrigger>
          <TabsTrigger value="product">Produktkort</TabsTrigger>
          <TabsTrigger value="content">Innehåll</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="basics">
          <div className="ac-v2-editor-panel">
            <div className="ac-v2-field-grid ac-v2-field-grid--two">
              <label className="ac-v2-field">
                <span>Titel</span>
                <input value={workingCourse.title} onChange={(e) => updateCourseField("title", e.target.value)} />
              </label>

              <label className="ac-v2-field">
                <span>Status</span>
                <select
                  value={String(workingCourse.status)}
                  onChange={(e) => updateCourseField("status", Number(e.target.value) as CourseStatus)}
                >
                  {COURSE_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="ac-v2-field">
                <span>Kategori</span>
                <select value={workingCourse.category ?? DEFAULT_COURSE_CATEGORY} onChange={(e) => updateCategory(e.target.value)}>
                  {COURSE_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="ac-v2-field">
              <span>Beskrivning</span>
              <CKEditorFull
                value={workingCourse.description}
                onChange={(value) => updateCourseField("description", value)}
              />
            </label>

            <label className="ac-v2-field ac-v2-field--checkbox">
              <input
                type="checkbox"
                checked={workingCourse.isStandalone}
                readOnly
                disabled
              />
              <span>Självständig kurs (styrt av kategori)</span>
            </label>
          </div>
        </TabsContent>

        <TabsContent value="product">
          {workingCourse.isStandalone ? (
            <div className="ac-v2-product-layout">
              <div className="ac-v2-editor-panel">
                <div className="ac-v2-product-status-banner">
                  <span className="ac-v2-editor-pill ac-v2-editor-pill--slate">Produktstatus</span>
                  <strong>{productStatusLabel}</strong>
                  <span>Det här är produktkortets status, inte kursens övergripande status.</span>
                </div>

                <div className="ac-v2-field-grid ac-v2-field-grid--two">
                  <label className="ac-v2-field">
                    <span>Pris</span>
                    <input value={productCard.price} onChange={(e) => setProductCard(prev => ({ ...prev, price: e.target.value }))} />
                  </label>

                  <label className="ac-v2-field">
                    <span>Instruktör</span>
                    <input value={productCard.instructor} onChange={(e) => setProductCard(prev => ({ ...prev, instructor: e.target.value }))} />
                  </label>
                </div>

                <div className="ac-v2-field-grid ac-v2-field-grid--two">
                  <label className="ac-v2-field">
                    <span>Betyg</span>
                    <input type="number" min="0" max="5" step="0.1" value={productCard.rating} onChange={(e) => setProductCard(prev => ({ ...prev, rating: Number(e.target.value) }))} />
                  </label>

                  <label className="ac-v2-field">
                    <span>Produktstatus</span>
                    <select value={String(productCard.status)} onChange={(e) => setProductCard(prev => ({ ...prev, status: Number(e.target.value) }))}>
                      <option value="1">Publicerad</option>
                      <option value="0">Utkast</option>
                      <option value="2">Arkiverad</option>
                    </select>
                  </label>
                </div>

                <div className="ac-v2-field-grid ac-v2-field-grid--two">
                  <label className="ac-v2-field">
                    <span>Thumbnail</span>
                    <input type="file" accept="image/*" onChange={(e) => updateImage("thumbnail", e.target.files?.[0])} />
                  </label>

                  <label className="ac-v2-field">
                    <span>Banner</span>
                    <input type="file" accept="image/*" onChange={(e) => updateImage("banner", e.target.files?.[0])} />
                  </label>
                </div>

                <div className="ac-v2-field-grid ac-v2-field-grid--two">
                  <label className="ac-v2-field">
                    <span>Stripe status</span>
                    <input value={productCard.stripeProductId ? "Synkad" : "Inte synkad"} readOnly />
                  </label>

                  <label className="ac-v2-field">
                    <span>Stripe price id</span>
                    <input value={productCard.stripePriceId ?? ""} readOnly />
                  </label>
                </div>

                <div className="ac-v2-field-grid ac-v2-field-grid--two">
                  <label className="ac-v2-field">
                    <span>Stripe product id</span>
                    <input value={productCard.stripeProductId ?? ""} readOnly />
                  </label>
                </div>
              </div>

              <div className="ac-v2-product-preview-panel">
                <div className="ac-v2-product-preview-header">
                  <h3>Förhandsgranskning</h3>
                  <p>Komprimerad och expanderad vy</p>
                </div>

                <div className="ac-v2-product-preview-grid">
                  <div className="ac-v2-product-preview-card">
                    <CourseProductCard
                      title={workingCourse.title || "Ny kurs"}
                      description={workingCourse.description || "Beskrivning visas här"}
                      totalDuration={totalDuration}
                      chaptersCount={workingCourse.chapters.length}
                      enrolledUsers={workingCourse.enrolledUsers}
                      status={productCard.status as CourseStatus}
                      variant="compact"
                      price={productCard.price}
                      rating={productCard.rating}
                      instructor={productCard.instructor}
                      thumbnailImage={productCard.thumbnailImageUrl}
                      bannerImage={productCard.bannerImageUrl}
                    />
                  </div>

                  <div className="ac-v2-product-preview-card ac-v2-product-preview-card--wide">
                    <CourseProductCard
                      title={workingCourse.title || "Ny kurs"}
                      description={workingCourse.description || "Beskrivning visas här"}
                      totalDuration={totalDuration}
                      chaptersCount={workingCourse.chapters.length}
                      enrolledUsers={workingCourse.enrolledUsers}
                      status={productCard.status as CourseStatus}
                      variant="expanded"
                      price={productCard.price}
                      rating={productCard.rating}
                      instructor={productCard.instructor}
                      thumbnailImage={productCard.thumbnailImageUrl}
                      bannerImage={productCard.bannerImageUrl}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ac-v2-editor-panel">
              <p>Intern kurs: inget produktkort eller Stripe-synk krävs.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="content">
          <div className="ac-v2-editor-panel ac-v2-editor-panel--content">
            {workingCourse.id > 0 ? (
              <ChapterForm
                course={workingCourse}
                onChapterAdded={(updated) => {
                  setWorkingCourse(structuredClone(updated));
                  setSnapshotCourse(structuredClone(updated));
                }}
              />
            ) : (
              <p>Spara kursen först för att kunna lägga till kapitel.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
